import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ==================== WEBINAR APIs ====================

export const getWebinars = async (filters = {}) => {
  try {
    const response = await api.get('/webinars', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Error fetching webinars:', error);
    throw error;
  }
};

export const getWebinarById = async (webinarId) => {
  try {
    const response = await api.get(`/webinars/${webinarId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching webinar:', error);
    throw error;
  }
};

export const getSpeakers = async () => {
  try {
    const response = await api.get('/speakers');
    return response.data;
  } catch (error) {
    console.error('Error fetching speakers:', error);
    throw error;
  }
};

export const getCategories = async () => {
  try {
    const response = await api.get('/categories');
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

export const getTags = async () => {
  try {
    const response = await api.get('/tags');
    return response.data;
  } catch (error) {
    console.error('Error fetching tags:', error);
    throw error;
  }
};

// ==================== LEAD APIs ====================

export const submitLead = async (leadData) => {
  try {
    const response = await api.post('/leads', leadData);
    return response.data;
  } catch (error) {
    console.error('Error submitting lead:', error);
    throw error;
  }
};

// ==================== ANALYTICS APIs ====================

export const trackEvent = async (eventData) => {
  try {
    const response = await api.post('/analytics/track', eventData);
    return response.data;
  } catch (error) {
    console.error('Error tracking event:', error);
    // Don't throw error for analytics - fail silently
    return null;
  }
};

export const getWebinarAnalytics = async (webinarId) => {
  try {
    const response = await api.get(`/analytics/webinar/${webinarId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching analytics:', error);
    throw error;
  }
};

// ==================== HELPER FUNCTIONS ====================

// Generate or retrieve session ID
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('webinar_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('webinar_session_id', sessionId);
  }
  return sessionId;
};

// Track page impression
export const trackImpression = async (webinarId) => {
  try {
    if (!webinarId) return null;
    return await trackEvent({
      webinar_id: webinarId,
      event_type: 'impression',
      session_id: getSessionId(),
    });
  } catch (error) {
    console.error('Error tracking impression:', error);
    return null;
  }
};

// Track unlock click
export const trackUnlockClick = async (webinarId) => {
  try {
    if (!webinarId) return null;
    return await trackEvent({
      webinar_id: webinarId,
      event_type: 'unlock_click',
      session_id: getSessionId(),
    });
  } catch (error) {
    console.error('Error tracking unlock click:', error);
    return null;
  }
};

// Track webinar unlocked event (fires once per session on first form submission)
export const trackWebinarUnlocked = async (webinarId, title, topic, sourceUrl) => {
  try {
    if (!webinarId) return null;
    return await trackEvent({
      webinar_id: webinarId,
      event_type: 'webinar_unlocked',
      session_id: getSessionId(),
      metadata: {
        webinar_id: webinarId,
        title: title || '',
        topic: topic || '',
        source_url: sourceUrl || window.location.href,
      },
    });
  } catch (error) {
    console.error('Error tracking webinar unlocked:', error);
    return null;
  }
};

// ==================== SESSION LEAD HELPERS ====================

const SESSION_LEAD_KEY = 'session_lead_data';

// Store lead data for current browser session only (clears on tab/browser close)
export const setSessionLead = (leadData) => {
  sessionStorage.setItem(SESSION_LEAD_KEY, JSON.stringify(leadData));
};

// Get stored session lead data (null if not submitted yet or new session)
export const getSessionLead = () => {
  const stored = sessionStorage.getItem(SESSION_LEAD_KEY);
  return stored ? JSON.parse(stored) : null;
};

// Check if user has submitted the lead form in this session
export const hasSessionLead = () => {
  return !!sessionStorage.getItem(SESSION_LEAD_KEY);
};

// Track video play with detailed data
export const trackVideoPlay = async (webinarId, videoTime = 0) => {
  try {
    if (!webinarId) return null;
    return await trackEvent({
      webinar_id: webinarId,
      event_type: 'video_play',
      session_id: getSessionId(),
      timestamp: new Date().toISOString(),
      video_time: Math.round(videoTime),
      user_agent: navigator.userAgent,
      page_url: window.location.href,
    });
  } catch (error) {
    console.error('Error tracking video play:', error);
    return null;
  }
};

// Track video complete with detailed data
export const trackVideoComplete = async (webinarId, videoTime = 0) => {
  try {
    if (!webinarId) return null;
    return await trackEvent({
      webinar_id: webinarId,
      event_type: 'video_complete',
      session_id: getSessionId(),
      timestamp: new Date().toISOString(),
      video_time: Math.round(videoTime),
      user_agent: navigator.userAgent,
      page_url: window.location.href,
    });
  } catch (error) {
    console.error('Error tracking video complete:', error);
    return null;
  }
};

// Track video progress milestones with detailed data
export const trackVideoProgress = async (webinarId, percentage, videoTime = 0) => {
  try {
    if (!webinarId) return null;
    return await trackEvent({
      webinar_id: webinarId,
      event_type: `video_progress_${percentage}`,
      session_id: getSessionId(),
      timestamp: new Date().toISOString(),
      video_time: Math.round(videoTime),
      user_agent: navigator.userAgent,
      page_url: window.location.href,
      metadata: { percentage },
    });
  } catch (error) {
    console.error('Error tracking video progress:', error);
    return null;
  }
};

// ==================== ANALYTICS APIs (admin-authenticated) ====================

const ADMIN_KEY_STORAGE = 'analytics_admin_key';

export const setAnalyticsAdminKey = (key) => {
  sessionStorage.setItem(ADMIN_KEY_STORAGE, key);
};

export const getAnalyticsAdminKey = () => {
  return sessionStorage.getItem(ADMIN_KEY_STORAGE);
};

export const clearAnalyticsAdminKey = () => {
  sessionStorage.removeItem(ADMIN_KEY_STORAGE);
};

const adminHeaders = () => {
  const key = getAnalyticsAdminKey();
  return key ? { Authorization: `Bearer ${key}` } : {};
};

export const getAnalyticsEvents = async (filters = {}) => {
  try {
    const response = await api.get('/analytics/events', { params: filters, headers: adminHeaders() });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) throw error;
    console.error('Error fetching analytics events:', error);
    return { success: false, events: [], count: 0 };
  }
};

export const getAnalyticsSummary = async () => {
  try {
    const response = await api.get('/analytics/summary', { headers: adminHeaders() });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) throw error;
    console.error('Error fetching analytics summary:', error);
    return { 
      success: false, 
      summary: { total_plays: 0, total_completions: 0, completion_rate: 0 },
      dropoff: { at_25: 0, at_50: 0, at_75: 0, before_complete: 0 },
      funnel: [],
      top_webinars: []
    };
  }
};

export const getAnalyticsByRegion = async (region = null) => {
  try {
    const params = region ? { region } : {};
    const response = await api.get('/analytics/by-region', { params, headers: adminHeaders() });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) throw error;
    console.error('Error fetching regional analytics:', error);
    return { 
      success: false, 
      regions: [],
      available_regions: []
    };
  }
};

export const getAvailableRegions = async () => {
  try {
    const response = await api.get('/analytics/regions', { headers: adminHeaders() });
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) throw error;
    console.error('Error fetching regions:', error);
    return { success: false, regions: [] };
  }
};

export default api;
